/**
 * The encryption under the copy of a person's work this browser keeps.
 *
 * A farm PC is shared through the day, and the copy holds stock values and
 * pay. So everything kept is sealed with a **data key** of the person's own,
 * and the data key itself is only ever stored wrapped (.docs/023):
 *
 * - **by their password**, which is what lets them in with no connection — a
 *   wrong password simply fails to unwrap it;
 * - **by their server key** (`GET /settings/offline-key`), which only the
 *   server hands out and only to them. This is what keeps a day's unsent work
 *   readable after an administrator resets a password nobody remembers, and
 *   what opens the copy on an ordinary reload, when nobody types a password.
 *
 * WebCrypto throughout: AES-GCM 256 for sealing and wrapping, PBKDF2 with
 * SHA-256 to turn a password into a key.
 */

/** A value sealed with AES-GCM: a fresh nonce, and what it sealed. */
export interface Sealed {
  iv: string;
  data: string;
}

/**
 * PBKDF2 rounds for a password. OWASP's figure for SHA-256; about half a
 * second on a slow farm PC, once per sign-in. Kept with the wrapped key, so it
 * can be raised later without stranding what was wrapped before.
 */
export const PASSWORD_ROUNDS = 600_000;

const subtle = () => globalThis.crypto.subtle;

export function randomBase64(bytes: number): string {
  return toBase64(globalThis.crypto.getRandomValues(new Uint8Array(bytes)));
}

/** A new data key. Extractable, so it can be wrapped by both keys. */
export function newDataKey(): Promise<CryptoKey> {
  return subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

/** The key a password stands for, with the salt kept beside the wrapped data key. */
export async function passwordKey(password: string, salt: string, rounds = PASSWORD_ROUNDS): Promise<CryptoKey> {
  const material = await subtle().importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, [
    'deriveKey',
  ]);
  return subtle().deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: fromBase64(salt), iterations: rounds },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['wrapKey', 'unwrapKey'],
  );
}

/** The person's server key, as the server sends it. */
export function serverKey(base64: string): Promise<CryptoKey> {
  return subtle().importKey('raw', fromBase64(base64), { name: 'AES-GCM' }, false, ['wrapKey', 'unwrapKey']);
}

export async function wrapDataKey(dataKey: CryptoKey, by: CryptoKey): Promise<Sealed> {
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const data = await subtle().wrapKey('raw', dataKey, by, { name: 'AES-GCM', iv });
  return { iv: toBase64(iv), data: toBase64(new Uint8Array(data)) };
}

/** Unwraps a data key; rejects when the key used is not the one that wrapped it. */
export function unwrapDataKey(wrapped: Sealed, by: CryptoKey): Promise<CryptoKey> {
  return subtle().unwrapKey(
    'raw',
    fromBase64(wrapped.data),
    by,
    { name: 'AES-GCM', iv: fromBase64(wrapped.iv) },
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
}

export async function seal(dataKey: CryptoKey, value: unknown): Promise<Sealed> {
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const plain = new TextEncoder().encode(JSON.stringify(value));
  const data = await subtle().encrypt({ name: 'AES-GCM', iv }, dataKey, plain);
  return { iv: toBase64(iv), data: toBase64(new Uint8Array(data)) };
}

export async function open<T>(dataKey: CryptoKey, sealed: Sealed): Promise<T> {
  const plain = await subtle().decrypt({ name: 'AES-GCM', iv: fromBase64(sealed.iv) }, dataKey, fromBase64(sealed.data));
  return JSON.parse(new TextDecoder().decode(plain)) as T;
}

function toBase64(bytes: Uint8Array): string {
  let text = '';
  for (const byte of bytes) text += String.fromCharCode(byte);
  return btoa(text);
}

function fromBase64(text: string): Uint8Array<ArrayBuffer> {
  const raw = atob(text);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}
